import {
  Anchor,
  Badge,
  Box,
  Card,
  Divider,
  Grid,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconBrandWhatsapp,
  IconDeviceTablet,
  IconLayoutGrid,
  IconMail,
  IconMessage,
  IconPhone,
  IconPhoto,
  IconSearch,
  IconUserCheck,
  IconWebhook,
} from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import FunnelChart from '../../../components/Charts/FunnelChart';
import ChangeIndicator from '../../../components/Dashboard/ChangeIndicator';
import MetricTile from '../../../components/Dashboard/MetricTile';
import ErrorMessage from '../../../components/ErrorMessage';
import Spinner from '../../../components/Spinner';
import { CampaignTypes } from '../../../constants/pageTitles';
import {
  EM_DASH,
  bannerHref,
  campaignHref,
  formatConversionMetric,
  formatCount,
  formatCurrency,
  formatRatio,
  hasLiveState,
  isComparableAcrossPeriods,
  productBlockHref,
} from '../../../helpers/atAGlance';
import { attributedPurchasesHref } from '../../../helpers/attributedPurchases';
import { funnelMeasurementCaption } from '../../../helpers/funnels';
import { dateToEndOfDay, dateToStartOfDay } from '../../../helpers/dateUtils';
import { configuredChannels } from '../../../helpers/domainChannels';
import getErrorMessage from '../../../helpers/getErrorMessage';
import useFetch from '../../../helpers/useFetch';

// Icon and short label per channel, kept together so a tile, a campaign row and a
// message row can never disagree about how a channel is drawn.
const CHANNEL_META = {
  email: { label: 'Email', Icon: IconMail },
  pushNotification: { label: 'Push', Icon: IconDeviceTablet },
  textMessage: { label: 'SMS', Icon: IconMessage },
  viberMessage: { label: 'Viber', Icon: IconPhone },
  whatsappMessage: { label: 'WhatsApp', Icon: IconBrandWhatsapp },
  webhook: { label: 'Webhook', Icon: IconWebhook },
};

// On-site personalization surfaces, reported separately from the outbound message
// channels because they have no per-domain credentials to gate on — every domain
// can attribute revenue to them.
const SOURCE_META = [
  { key: 'recommender', label: 'Product blocks', Icon: IconLayoutGrid },
  { key: 'banner', label: 'Banner blocks', Icon: IconPhoto },
  { key: 'search', label: 'Search as you type', Icon: IconSearch },
  { key: 'salesAgent', label: 'Sales agent', Icon: IconUserCheck },
];

// Why a row shows no percent change. Surfaced as a tooltip so the em dash is not
// cryptic in a column whose every other value is a percentage.
const changeUnavailableReason = (campaign) => {
  if (campaign.id == null) {
    return 'This campaign has been deleted, so there is nothing to match it against in the previous period';
  }
  return isComparableAcrossPeriods(campaign)
    ? 'The previous period did not report this campaign, so its revenue then is unknown'
    : 'A one-off sends once, so comparing it across periods would measure its send date';
};

const formatDay = (isoDate) => DateTime.fromISO(isoDate).toFormat('d LLL yyyy');
const formatSentAt = (timestamp) => DateTime.fromISO(timestamp).toFormat('d LLL yyyy, HH:mm');

// A channel the API has added since CHANNEL_META was last updated names itself
// instead of crashing the tab.
const ChannelIcon = ({ channel }) => {
  const meta = CHANNEL_META[channel];
  return meta ? <meta.Icon size={16} role="img" aria-label={meta.label} /> : <Text size="xs">{channel}</Text>;
};

// One ranked on-site surface: which product blocks, or which banners, earned the
// most. Both are the same shape apart from how a row is addressed, so they share
// this rather than being written twice.
//
// No "not comparable" case here, unlike the campaign tables: an on-site surface
// has no send date, so every row is comparable in principle. What varies is only
// whether the previous period's cut happens to contain it.
const TopSurfaceTable = ({ title, description, rows, previousRows, emptyMessage, hrefFor, currency, valueLabel }) => {
  const previousById = new Map((previousRows || []).filter((row) => row.id != null).map((row) => [row.id, row]));

  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Title order={5}>{title}</Title>
      <Text size="sm" c="dimmed" mb="md">
        {description}
      </Text>
      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          {emptyMessage}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={420}>
          <Table striped highlightOnHover aria-label={title}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>{valueLabel}</Table.Th>
                <Table.Th w={90}>Change</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* Same as the campaign tables: a deleted surface arrives with a null
                  id, so the index keys the row and the lookup map has already
                  dropped nulls to keep unrelated deletions from colliding. */}
              {rows.map((row, index) => {
                const href = hrefFor(row);
                const previousRow = row.id == null ? undefined : previousById.get(row.id);
                return (
                  <Table.Tr key={index}>
                    <Table.Td>
                      {href === null ? (
                        <Text size="sm">{row.name || 'Deleted'}</Text>
                      ) : (
                        <Anchor size="sm" href={href}>
                          {row.name}
                        </Anchor>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatCurrency(row.revenue, currency)}</Text>
                      <Text size="xs" c="dimmed">
                        {formatCount(row.conversions)} conv.
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {previousRow ? (
                        <ChangeIndicator value={row.revenue} previousValue={previousRow.revenue} />
                      ) : (
                        <Tooltip
                          label={
                            row.id == null
                              ? 'This has been deleted, so there is nothing to match it against in the previous period'
                              : 'This was not in the previous period’s top five, so what it earned then is unknown'
                          }
                          multiline
                          w={260}
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                        >
                          <Text size="xs" c="dimmed" tabIndex={0} style={{ cursor: 'help' }}>
                            {EM_DASH}
                          </Text>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Card>
  );
};

const AtAGlanceTab = ({ startDate, endDate, prevStartDate, prevEndDate }) => {
  const fetch = useFetch();
  const { domain } = useSelector((state) => state.domain);
  const whatsappAccount = useSelector((state) => state.whatsapp.account);

  const [data, setData] = useState();
  const [error, setError] = useState();

  // The endpoint is range-pure - it knows nothing about a previous period - so the
  // previous range is a second call and every delta below is diffed client-side.
  useEffect(() => {
    let ignore = false;
    setData(undefined);
    setError(undefined);

    // The funnel is by far the heaviest thing the endpoint computes — a
    // windowFunnel scan over `events` — and it is opt-IN there, so only the call
    // that actually renders one asks for it. The previous-period and back-fill
    // calls simply do not, rather than opting out of a default.
    const url = (start, end, { campaignIds, includeFunnel = false } = {}) => {
      const params = new URLSearchParams();
      if (campaignIds?.length) {
        params.set('campaignIds', campaignIds.join(','));
      }
      if (includeFunnel) {
        params.set('includeFunnel', 'true');
      }
      const query = params.toString();

      return (
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/atAGlance/${encodeURIComponent(
          dateToStartOfDay(start, 'local')
        )}/${encodeURIComponent(dateToEndOfDay(end, 'local'))}` + (query ? `?${query}` : '')
      );
    };

    // Both ranges unfiltered, in parallel. Every period-wide figure on this tab —
    // the two headline tiles and the whole attributed grid — is read from these two
    // responses, so neither may be narrowed by a campaign filter.
    Promise.all([fetch(url(startDate, endDate, { includeFunnel: true })), fetch(url(prevStartDate, prevEndDate))])
      .then(([current, previous]) => {
        if (ignore) {
          return;
        }

        // `topCampaigns` is a ranked cut, so a campaign inside this period's cut but
        // outside the previous one is unmeasured there rather than zero. Ask about
        // exactly those, and only those: on a domain with fewer always-on campaigns
        // than the cut there is nothing missing and no third request at all.
        const ranked = new Set(
          (previous?.topCampaigns || []).map((campaign) => campaign.id).filter((id) => id != null)
        );
        const missing = (current?.topCampaigns || [])
          .map((campaign) => campaign.id)
          .filter((id) => id != null && !ranked.has(id));

        // Paint on the two period-wide responses alone. Awaiting the lookup here
        // would put every tile behind a second serial round trip on any domain
        // whose ranking churned — which is most of them, most of the time — for a
        // column none of those tiles read.
        setData({ current, previous, backfill: [], backfillPending: missing.length > 0 });

        if (!missing.length) {
          return;
        }

        // An enrichment, not a dependency: if it fails, the deltas it would have
        // filled stay at the em dash and nothing else on the tab is affected.
        fetch(url(prevStartDate, prevEndDate, { campaignIds: missing }))
          .then((response) => response?.topCampaigns || [])
          .catch(() => [])
          .then((backfill) => {
            if (!ignore) {
              setData((previousState) => previousState && { ...previousState, backfill, backfillPending: false });
            }
          });
      })
      .catch((err) => {
        if (!ignore) setError(err);
      });

    return () => {
      ignore = true;
    };
  }, [domain.accountId, domain.id, startDate, endDate, prevStartDate, prevEndDate, fetch]);

  if (error) {
    return <ErrorMessage message={getErrorMessage(error)} />;
  }

  if (data === undefined) {
    return <Spinner />;
  }

  const currency = domain.currency;
  const {
    conversionMetric = {},
    summary = {},
    attributedRevenue = {},
    topCampaigns = [],
    recentCampaigns = [],
    topProductBlocks = [],
    topBanners = [],
  } = data.current;
  const {
    summary: prevSummary = {},
    attributedRevenue: prevAttributedRevenue = {},
    topCampaigns: prevCampaigns = [],
    topProductBlocks: prevProductBlocks = [],
    topBanners: prevBanners = [],
  } = data.previous;

  const { byMode = {}, byChannel = {} } = attributedRevenue;
  const prevByMode = prevAttributedRevenue.byMode || {};
  const prevByChannel = prevAttributedRevenue.byChannel || {};
  // Sibling of attributedRevenue, not part of it: an on-site surface needs no
  // campaign to earn revenue, so these can exceed attributed revenue or be
  // non-zero while it is zero. Never divide one by the other.
  //
  // Not `bySource` — that name is taken, by a different population: RevenueTab
  // reads /stats' `purchases.grouped.bySource`, which is revenue by EXTERNAL utm
  // source. Two tabs of the same dashboard, so the names have to stay distinct.
  const onSiteRevenue = data.current.revenueByOnSiteSource;
  const prevOnSiteRevenue = data.previous.revenueByOnSiteSource || {};
  // A deleted campaign arrives with id: null, so keying on it would collapse
  // every deleted campaign from the previous period into one map entry and
  // match unrelated deletions against each other. The backfill rows are the
  // campaigns the previous period's ranked cut did not reach.
  const prevCampaignsById = new Map(
    [...prevCampaigns, ...(data.backfill || [])]
      .filter((campaign) => campaign.id != null)
      .map((campaign) => [campaign.id, campaign])
  );

  // A row whose previous-period figure is still in flight. Rendering the em dash
  // for it would assert the figure is unknowable when it is merely not back yet.
  const awaitingPrevious = (campaign) =>
    Boolean(data.backfillPending) && campaign.id != null && isComparableAcrossPeriods(campaign);

  const conversionLabel = formatConversionMetric(conversionMetric);
  const channels = configuredChannels({ domain, whatsappAccount });
  const linkBase = { accountId: domain.accountId, domainId: domain.id };
  const funnel = data.current.funnel;
  const funnelSteps = (funnel?.steps || []).map((step) => ({
    label: step.name,
    value: step.count,
    percentOfPrevious: step.percentOfPrevious,
  }));
  // A funnel whose FIRST step is empty has nothing downstream by construction, so
  // every bar is 0 and the chart says nothing. Naming the untracked event turns a
  // chart that looks broken into an integration prompt — the default funnel opens
  // on `pageView`, which a domain that does not track page hits never emits.
  const funnelUntrackedStep = funnelSteps.length > 0 && funnelSteps[0].value === 0 ? funnelSteps[0].label : null;
  // Shared with the Funnels tab so the two cards cannot describe the same
  // measurement differently — they already had, which is how "over the selected
  // range" and "within the selected period" ended up two clicks apart. On the
  // unavailable path the window is null and only the ", within …" clause drops;
  // the sentence and the Releva line below still render, above the separate
  // "could not be measured" message.
  const funnelCaption = funnelMeasurementCaption(funnel || {});

  // The share the bar draws comes from the API's own attributedShare
  // (rate(attributedRevenue, totalRevenue), at-a-glance-reporter.js:790), which is
  // the same number the tile hint prints — so the two cannot diverge. Recomputing
  // it here from the two amounts is what would let them.
  //
  // null when there is no total to be a share OF: rate() returns 0 for a zero
  // denominator, so drawing it anyway would assert "0% attributed" for a domain
  // that simply took no money in this range.
  const attributedPercent =
    summary.totalRevenue > 0 && typeof summary.attributedShare === 'number'
      ? Math.min(Math.max(summary.attributedShare * 100, 0), 100)
      : null;
  // Attributed revenue is a subset of the same orders the total counts, so this
  // should never go negative. Clamped because if it ever did — a data fault, not a
  // rounding one — an inverted section would draw as though Releva earned more than
  // the store took, which is the one reading this card must never produce.
  const unattributedRevenue = Math.max((summary.totalRevenue || 0) - (summary.attributedRevenue || 0), 0);

  // Three groups, because they answer three different questions and mixing them
  // in one grid invited exactly that confusion: "what did each campaign mode
  // earn", "what did each way of messaging them earn", and "what did the site
  // itself earn with no message involved".
  const overallTiles = [
    { key: 'oneOff', label: 'One-off', value: byMode.oneOff, previousValue: prevByMode.oneOff },
    {
      key: 'continuousAndTriggered',
      label: 'Continuous & triggered',
      value: byMode.continuousAndTriggered,
      previousValue: prevByMode.continuousAndTriggered,
    },
  ];

  const channelTiles = channels.map((channel) => ({
    key: channel,
    label: CHANNEL_META[channel].label,
    icon: <ChannelIcon channel={channel} />,
    value: byChannel[channel],
    previousValue: prevByChannel[channel],
  }));

  // Only rendered once the endpoint actually reports these. Rendering them
  // against a missing field would show four confident zeroes for surfaces that
  // simply have not been measured yet.
  const onSiteTiles = onSiteRevenue
    ? SOURCE_META.map((source) => ({
        key: source.key,
        label: source.label,
        icon: <source.Icon size={16} role="img" aria-label={source.label} />,
        value: onSiteRevenue[source.key],
        previousValue: prevOnSiteRevenue[source.key],
      }))
    : [];

  const tileGrid = (tiles) => (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
      {tiles.map((tile) => (
        <MetricTile
          key={tile.key}
          label={tile.label}
          icon={tile.icon}
          value={tile.value}
          previousValue={tile.previousValue}
          currency={currency}
          hint={tile.hint}
        />
      ))}
    </SimpleGrid>
  );

  return (
    <Stack gap="md" mt="md">
      <Group gap="xs">
        <Text size="sm" c="dimmed">
          Conversion metric:
        </Text>
        <Text size="sm" fw={600}>
          {conversionLabel}
        </Text>
      </Group>

      <Card withBorder radius="md" padding="md">
        {/* No title or date header: the range selector above states the period and
            the first tile names the figure, so a header repeating both was noise.
            The range is restated in the caption at the bottom instead. */}
        {/* Attributed leads and total follows, rather than the other way round.
            Total store revenue is the one figure here that Releva neither earned
            nor measures authoritatively — the customer already knows it from their
            store, and it is the number most likely to disagree with that store
            (tracking gaps, refund timing, currency). Leading a page about Releva's
            value with someone else's number, and specifically with the number most
            open to dispute, invited doubt over everything under it. It stays,
            because attributed revenue means nothing without its denominator, but
            it stays as the denominator. */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {/* The link lives inside the tile it backs up: "Attributed" is the most
              challengeable word on this page, and a reader who doubts the number
              should find the way to check it on the number itself — not in a
              card corner where it read as a link to every order in the store.
              It carries the range: the dashboard holds it in component state, not
              in the URL, so a details page opened without it would default to its
              own period and quietly report a different figure than this tile. */}
          <MetricTile
            big
            label="Attributed revenue"
            value={summary.attributedRevenue}
            previousValue={prevSummary.attributedRevenue}
            currency={currency}
            hint={`${formatRatio(summary.attributedShare)} of total store revenue`}
            footer={
              <Anchor
                size="xs"
                display="inline-block"
                mt="xs"
                href={attributedPurchasesHref({
                  accountId: domain.accountId,
                  domainId: domain.id,
                  startDate,
                  endDate,
                })}
              >
                View attributed orders
              </Anchor>
            }
          />
          <MetricTile
            label="Total store revenue"
            value={summary.totalRevenue}
            previousValue={prevSummary.totalRevenue}
            currency={currency}
            hint="Everything your store took, Releva involved or not"
          />
        </SimpleGrid>

        {/* The two tiles state both figures; this states the RELATIONSHIP, which is
            the thing the card is actually about and the one thing two separate
            numbers cannot show. A part-of-whole with exactly two parts wants a
            length, not an angle: the eye reads "this much of that" off a shared
            baseline far better than off two arcs, which is also why the donut came
            off SummaryTopCard earlier in this PR.

            Both sections derive from `attributedShare`, which the API computes as
            rate(attributedRevenue, totalRevenue) (at-a-glance-reporter.js:790) —
            the same number the tile's hint prints. So the bar cannot disagree with
            the text beside it, which it could if this recomputed the ratio from the
            two amounts.

            Only drawn when there is a total to be a share OF. With no store revenue
            the bar has no meaning: rate() returns 0 for a zero denominator, so an
            empty track would assert "0% attributed" for a domain that simply took
            no money in the range. */}
        {attributedPercent !== null && (
          <Paper withBorder p="md" mt="lg">
            <Progress.Root size={20}>
              <Tooltip label={`Attributed to Releva: ${formatCurrency(summary.attributedRevenue, currency)}`}>
                <Progress.Section value={attributedPercent} color="primary.6" />
              </Tooltip>
              <Tooltip label={`Rest of your store: ${formatCurrency(unattributedRevenue, currency)}`}>
                <Progress.Section value={100 - attributedPercent} color="gray.3" />
              </Tooltip>
            </Progress.Root>
            {/* Both keys on the left, next to each other, rather than pinned to
                opposite ends of the card. Spread apart they read as two unrelated
                corner labels — and at a 7% share the right-hand one sits nowhere
                near the section it names anyway, since the grey runs most of the
                width.

                Each carries its own percentage as well as its amount: the share is
                the point of the bar, and printing it only in the tile above left the
                bar itself unlabelled at exactly the widths too narrow to hold a
                label.

                Both percentages come off `attributedPercent`, the same clamped basis
                the two sections are drawn from, so a legend key can never disagree
                with the section it names — which is what deriving the remainder as
                `1 - attributedShare` would allow in the one case the clamp exists
                for. */}
            <Group gap="lg" mt="sm" wrap="wrap">
              <Group gap={8} wrap="nowrap">
                <Box
                  w={10}
                  h={10}
                  style={{
                    borderRadius: 'var(--mantine-radius-xs)',
                    backgroundColor: 'var(--mantine-color-primary-6)',
                  }}
                />
                <Text size="xs" fw={600}>
                  {formatRatio(attributedPercent / 100)}
                </Text>
                <Text size="xs" c="dimmed">
                  attributed · {formatCurrency(summary.attributedRevenue, currency)}
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Box
                  w={10}
                  h={10}
                  style={{ borderRadius: 'var(--mantine-radius-xs)', backgroundColor: 'var(--mantine-color-gray-3)' }}
                />
                <Text size="xs" fw={600}>
                  {formatRatio((100 - attributedPercent) / 100)}
                </Text>
                <Text size="xs" c="dimmed">
                  rest of your store · {formatCurrency(unattributedRevenue, currency)}
                </Text>
              </Group>
            </Group>
          </Paper>
        )}
        {/* Said plainly, next to the number, rather than left for the reader to
            assume. "Attributed" is the single most challengeable word on this page
            and the challenge — "they would have bought anyway" — is fair. Naming
            the rule disarms more than a bigger number would.

            Which rule, though, is per domain: `campaignAttribution` is either
            'click' or 'openOrClick' (models/domain.js:309-313, set from Settings →
            Analytics), and 'openOrClick' is what a domain gets when the field is
            unset. So this cannot state one rule for everyone — on an opens domain
            a click-only sentence would describe a stricter figure than the one
            printed directly above it, which is worse than saying nothing. */}
        <Text size="xs" c="dimmed" mt="md">
          {formatDay(startDate)} – {formatDay(endDate)}. Attributed means the buyer{' '}
          {domain.campaignAttribution === 'click' ? 'clicked' : 'opened or clicked'} a Releva message, or clicked an
          on-site block, before ordering — within 7 days for messages, 1 day for on-site surfaces. It counts orders
          Releva took part in, not orders it can prove it caused.
        </Text>
      </Card>

      {funnel && (
        <Card withBorder radius="md" padding="md">
          <Group justify="space-between" align="flex-start" mb="md">
            <div>
              <Title order={5}>{funnel.name}</Title>
              {/* The population claim is the one that mattered here: sitting
                  among attribution tiles, an unqualified funnel reads as Releva's
                  funnel, and the seeded default runs on everyone who visited. A
                  reader flattered by that once and told the truth later resents
                  both readings.

                  What this sentence must NOT do is name a conversion window. An
                  earlier version did, on the reasoning that the range picks which
                  events are in scope while the window bounds how long a profile
                  has. The API says otherwise in as many words
                  (funnel-reporter.js:49-65): the window it uses IS the range, so
                  it "can never actually reject a chain". Naming it asserted an
                  enforcement that does not happen — see helpers/funnels.js. */}
              <Text size="sm" c="dimmed">
                {funnelCaption}
              </Text>
              <Text size="sm" c="dimmed">
                Counted whether or not Releva reached them — this is your funnel, not Releva’s contribution to it.
              </Text>
            </div>
            <Anchor
              size="xs"
              href={`#/accounts/${domain.accountId}/domains/${domain.id}/customerInsights?mainTab=funnels`}
            >
              Edit funnels
            </Anchor>
          </Group>
          {funnel.unavailable ? (
            <Text size="sm" c="dimmed">
              This funnel could not be measured for the selected period. A shorter date range usually helps — it is the
              slowest report on this page.
            </Text>
          ) : funnelUntrackedStep ? (
            <Text size="sm" c="dimmed">
              No <b>{funnelUntrackedStep}</b> events were recorded in this period, so the funnel has nothing to follow.
              Check that this event is being tracked, or edit the funnel to start from one that is.
            </Text>
          ) : (
            <>
              <FunnelChart data={funnelSteps} height={40 * funnelSteps.length + 40} />
              <Text size="xs" c="dimmed" mt="sm">
                The funnel&apos;s width is how many people reached each step, so it narrows as it drains, and the pale
                area either side is everyone lost since the first step. The percentage beside a step is how many of the
                step above it got that far — that is the number worth acting on, and the one to look for the biggest
                fall in.
              </Text>
            </>
          )}
        </Card>
      )}

      <Card withBorder radius="md" padding="md">
        <Title order={5} mb="md">
          Attributed revenue
        </Title>

        {tileGrid(overallTiles)}

        {channelTiles.length > 0 && (
          <>
            <Divider
              my="lg"
              labelPosition="left"
              label={
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Sent through campaigns
                </Text>
              }
            />
            {tileGrid(channelTiles)}
          </>
        )}

        {onSiteTiles.length > 0 && (
          <>
            <Divider
              my="lg"
              labelPosition="left"
              label={
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  On your website
                </Text>
              }
            />
            {tileGrid(onSiteTiles)}
          </>
        )}

        <Text size="xs" c="dimmed" mt="lg">
          These figures overlap and do not add up to attributed revenue: one order can be attributed to several surfaces
          at once, revenue earned on the website involves no campaign and so belongs to neither mode, and ads are
          counted in the total without a tile of their own.
        </Text>
      </Card>

      {/* Side by side: the two halves of the campaign population, read together
          rather than one under the other. `align="stretch"` plus h="100%" on the
          cards makes the shorter one match the taller, so the row has one bottom
          edge instead of a ragged one. */}
      <Grid gutter="md" align="stretch">
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Card withBorder radius="md" padding="md" h="100%">
            <Title order={5}>Top performing continuous &amp; triggered</Title>
            <Text size="sm" c="dimmed" mb="md">
              Always-on campaigns and workflows, ranked by attributed revenue. One-off sends are reported below.
            </Text>
            {topCampaigns.length === 0 ? (
              <Text size="sm" c="dimmed">
                No continuous or triggered campaign was attributed any revenue in this period.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={520}>
                <Table striped highlightOnHover aria-label="Top performing continuous and triggered">
                  <Table.Thead>
                    <Table.Tr>
                      {/* Widths pinned on the four narrow columns so the two that
                          need the room — the campaign name and the conversion figure
                          — take the remainder, instead of the browser's table
                          algorithm rationing it and squeezing a badge until it
                          clips. `Status` needs to hold "OFF": the theme leaves Badge
                          text uppercase, and the column was landing on just enough
                          width for "ON". */}
                      <Table.Th>Campaign</Table.Th>
                      <Table.Th w={72}>Status</Table.Th>
                      <Table.Th w={84}>Channels</Table.Th>
                      <Table.Th w={90}>Deliveries</Table.Th>
                      <Table.Th>{conversionLabel}</Table.Th>
                      <Table.Th w={90}>Change</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {/* campaign.id is null for a deleted campaign, so it cannot key the row
                      or look itself up in prevCampaignsById (which already drops null
                      ids, so unrelated deletions never collide there either). */}
                    {topCampaigns.map((campaign, index) => {
                      const isDeleted = campaign.id == null;
                      const previousCampaign = isDeleted ? undefined : prevCampaignsById.get(campaign.id);
                      // Absent from the previous top-N means "not measured", not "earned
                      // nothing": that list is a ranked cut, so a campaign that placed
                      // below it is invisible here. Treating absence as 0 would report a
                      // confident +100% for a campaign whose revenue actually fell — just
                      // by less than its neighbours'.
                      const comparable = isComparableAcrossPeriods(campaign) && previousCampaign !== undefined;
                      const href = campaignHref({ ...linkBase, campaign });
                      return (
                        <Table.Tr key={index}>
                          <Table.Td>
                            {href === null ? (
                              <Text size="sm">{campaign.name || 'Deleted campaign'}</Text>
                            ) : (
                              <Anchor size="sm" href={href}>
                                {campaign.name || 'Untitled campaign'}
                              </Anchor>
                            )}
                            {campaign.mode && (
                              <Text size="xs" c="dimmed">
                                {CampaignTypes[campaign.mode]}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {/* Blank for a one-off: `live` goes false as soon as its send
                              finishes, so "Off" would read as a fault when it
                              actually means the campaign completed.

                              "On"/"Off" rather than "Live"/"Not live": the column is
                              narrow enough that "Not live" was being clipped to
                              "NOT L…", which is worse than a shorter word. Two
                              letters also let the badge sit at its natural width
                              instead of setting the column's. */}
                            {hasLiveState(campaign) && (
                              <Badge variant="light" color={campaign.live ? 'green' : 'gray'} style={{ flexShrink: 0 }}>
                                {campaign.live ? 'On' : 'Off'}
                              </Badge>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              {(campaign.channels || []).map((channel) => (
                                <ChannelIcon key={channel} channel={channel} />
                              ))}
                            </Group>
                          </Table.Td>
                          <Table.Td>{formatCount(campaign.deliveries)}</Table.Td>
                          <Table.Td>
                            <Text size="sm">{formatCurrency(campaign.conversions?.value, currency)}</Text>
                            <Text size="xs" c="dimmed">
                              {formatCount(campaign.conversions?.count)} conv. ·{' '}
                              {formatCurrency(campaign.revenuePerRecipient, currency)} per delivery
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {/* A one-off campaign sends once, so it is very unlikely to have
                              run in both periods; a "change" would be measuring its send
                              date rather than its performance. */}
                            {comparable ? (
                              <ChangeIndicator
                                value={campaign.conversions?.value}
                                previousValue={previousCampaign.conversions?.value}
                              />
                            ) : (
                              // `events` and a focusable target because a Text is neither
                              // hoverable by keyboard nor by touch on its own, matching
                              // DataQualityTab's InfoDot.
                              <Tooltip
                                label={
                                  awaitingPrevious(campaign)
                                    ? 'Looking up this campaign’s revenue in the previous period'
                                    : changeUnavailableReason(campaign)
                                }
                                multiline
                                w={260}
                                withArrow
                                events={{ hover: true, focus: true, touch: true }}
                              >
                                <Text size="xs" c="dimmed" tabIndex={0} style={{ cursor: 'help' }}>
                                  {awaitingPrevious(campaign)
                                    ? '…'
                                    : isComparableAcrossPeriods(campaign)
                                      ? EM_DASH
                                      : 'Not comparable'}
                                </Text>
                              </Tooltip>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Card withBorder radius="md" padding="md" h="100%">
            <Title order={5}>Recent one-off campaigns</Title>
            <Text size="sm" c="dimmed" mb="md">
              The one-off campaigns and workflows that sent in this period, most recent first. Listed by send date,
              while the One-off figure above counts revenue by purchase date — so a send from before this period can
              earn revenue in it without appearing here.
            </Text>
            {recentCampaigns.length === 0 ? (
              <Text size="sm" c="dimmed">
                No one-off campaign sent in this period.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={520}>
                <Table striped highlightOnHover aria-label="Recent one-off campaigns">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Campaign</Table.Th>
                      <Table.Th>Channels</Table.Th>
                      <Table.Th>Open rate</Table.Th>
                      <Table.Th>Click rate</Table.Th>
                      <Table.Th>{conversionLabel}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {/* id is null for a campaign deleted since it sent, so it cannot key
                      the row. */}
                    {recentCampaigns.map((campaign, index) => {
                      const href = campaignHref({ ...linkBase, campaign });
                      return (
                        <Table.Tr key={index}>
                          <Table.Td>
                            {href === null ? (
                              <Text size="sm">{campaign.name || 'Deleted campaign'}</Text>
                            ) : (
                              <Anchor size="sm" href={href}>
                                {campaign.name || 'Untitled campaign'}
                              </Anchor>
                            )}
                            <Text size="xs" c="dimmed">
                              Sent on {formatSentAt(campaign.lastSentAt)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              {(campaign.channels || []).map((channel) => (
                                <ChannelIcon key={channel} channel={channel} />
                              ))}
                            </Group>
                          </Table.Td>
                          <Table.Td>{formatRatio(campaign.openRate)}</Table.Td>
                          <Table.Td>{formatRatio(campaign.clickRate)}</Table.Td>
                          <Table.Td>
                            <Text size="sm">{formatCurrency(campaign.conversions?.value, currency)}</Text>
                            <Text size="xs" c="dimmed">
                              {formatCount(campaign.conversions?.count)} conv. ·{' '}
                              {/* Both operands checked: a missing conversions.value over a
                                real recipient count is NaN, which formats as "NaN"
                                rather than the em dash a missing figure should get. */}
                              {formatCurrency(
                                campaign.recipients && campaign.conversions?.value != null
                                  ? campaign.conversions.value / campaign.recipients
                                  : null,
                                currency
                              )}{' '}
                              per recipient
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {/* The on-site half of the same question, paired the same way: the tiles
          above say product blocks and banners earned money, these say which ones,
          so the figure can be acted on rather than only noted. */}
      <Grid gutter="md" align="stretch">
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <TopSurfaceTable
            title="Top performing product blocks"
            description="Product blocks that earned the most in this period, highest first."
            rows={topProductBlocks}
            previousRows={prevProductBlocks}
            emptyMessage="No product block was attributed any revenue in this period."
            hrefFor={(block) => productBlockHref({ ...linkBase, block })}
            currency={currency}
            valueLabel={conversionLabel}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <TopSurfaceTable
            title="Top performing banner blocks"
            description="Banners that earned the most in this period, highest first."
            rows={topBanners}
            previousRows={prevBanners}
            emptyMessage="No banner was attributed any revenue in this period."
            hrefFor={(banner) => bannerHref({ ...linkBase, banner })}
            currency={currency}
            valueLabel={conversionLabel}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default AtAGlanceTab;
